// slice 02.1 · paso b.6l — barra de tarea del canvas de roadmap (`ComponentTaskFlow.jsx`): mover,
// redimensionar (duración), abrir el panel de detalle (doble clic), tooltip de fecha al soltar.
// Presentacional puro. El container calcula todo lo derivado de la fecha/duración/posición
// (`x`/`y`/`width`/`color`/`isMoving`) y pasa la fecha cruda del tooltip (`dropTooltipDate`) —
// esta hoja solo la formatea, ya que de todos modos necesita `useTranslation()` para el label
// "sin título".

import { MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function FlowTaskBar({ task, x, y, isMoving, width, color, isActive, isSelected, assigneeUser, dropTooltipDate, onMoveStart, onResizeStart, onOpen }) {
    const { t, i18n } = useTranslation('tasks');

    return (
        <div
            className={`roadmap-task-node ${isMoving ? 'is-moving' : ''}`}
            onPointerDown={onMoveStart}
            onDoubleClick={(e) => {
                e.stopPropagation();
                onOpen?.(e);
            }}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
            style={{
                position: 'absolute',
                left: x,
                top: y,
                display: 'flex',
                alignItems: 'center',
                zIndex: isMoving ? 50 : 20,
                cursor: isMoving ? 'grabbing' : 'grab',
                height: '32px',
                userSelect: 'none',
                touchAction: 'none',
                pointerEvents: 'auto'
            }}
        >
            {/* Active Task Highlight & Selection */}
            {(isActive || isSelected) && (
                <div style={{
                    position: 'absolute',
                    top: -6,
                    bottom: -6,
                    left: -14,
                    right: -10,
                    borderRadius: '12px',
                    border: isSelected ? '2px solid #0d99ff' : '2px dashed #3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.05)',
                    pointerEvents: 'none',
                    zIndex: 0
                }}></div>
            )}

            {/* Tooltip */}
            {dropTooltipDate && (
                <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginBottom: '3px',
                    backgroundColor: '#3b82f6',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    pointerEvents: 'none',
                    zIndex: 100,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>
                    {dropTooltipDate.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-ES', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
            )}
            {/* Colored Bar representing duration (BACKGROUND) */}
            <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: width,
                minWidth: '12px',
                backgroundColor: color,
                borderRadius: '9px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                opacity: 0.9,
                zIndex: 1,
                border: '1px solid rgba(255,255,255,0.1)'
            }}>
                {/* Resize handle */}
                <div
                    className="task-resize-handle"
                    onPointerDown={(e) => { e.stopPropagation(); onResizeStart(e); }}
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '16px',
                        cursor: 'ew-resize',
                        borderTopRightRadius: '9px',
                        borderBottomRightRadius: '9px',
                        zIndex: 10
                    }}></div>
            </div>

            {/* Avatar on the left (Foreground) */}
            <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                zIndex: 2,
                marginLeft: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
                position: 'relative'
            }}>
                {(assigneeUser['link-profile-img-thumb'] || assigneeUser['link-profile-img'] || assigneeUser.image) ? (
                    <img src={assigneeUser['link-profile-img-thumb'] || assigneeUser['link-profile-img'] || assigneeUser.image} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" loading="lazy" />
                ) : (
                    <span style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        {assigneeUser.nick ? assigneeUser.nick.substring(0, 2) : (assigneeUser.name ? assigneeUser.name.substring(0, 2) : '?')}
                    </span>
                )}
            </div>

            {/* Title and Comments on the right of Avatar (Foreground) */}
            <div
                className="task-title-interactive"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    zIndex: 2,
                    position: 'relative',
                    marginLeft: '4px',
                    padding: '2px 4px',
                    borderRadius: '4px'
                }}>
                <span style={{ color: '#fff', fontSize: '11px', fontWeight: '500', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                    {task.title || t('flow.untitledNode')}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', opacity: 0.8 }}>
                    <MessageSquare size={9} color="#fff" />
                    <span style={{ color: '#fff', fontSize: '9px' }}>{task.comments?.length || 0}</span>
                </div>
            </div>
        </div>
    );
}
